import React from "react";
import Modal from "react-bootstrap/Modal";
import { FiXCircle } from "react-icons/fi";
import { BlogCategoryDto } from "../../../../types/app/blogTypes";
import { TableComponent } from "../../../../shared/components/table";
import { LoadingItem } from "../../../../shared/components/loading";
import { AlertMsg } from "../../../../shared/components/alert-msg";
import AddIco from "../../../../assets/icons/add.png";
import styles from "../categories.module.css";

interface CategoryTableProps {
  categories: BlogCategoryDto[];
  onEdit: (category: BlogCategoryDto) => void;
  onDelete: (category: BlogCategoryDto) => void;
}

interface CategoriesContentProps {
  loading: boolean;
  error: string | null;
  categories: BlogCategoryDto[];
  operationSuccess: string | null;
  searchSlug: string;
  isSearching: boolean;
  showAddModal: boolean;
  showEditModal: boolean;
  showDeleteModal: boolean;
  selectedCategory: BlogCategoryDto | null;
  saving: boolean;
  errorMessage: string;
  onRetry: () => void;
  onSearchChange: (value: string) => void;
  onSearch: () => void;
  onClearSearch: () => void;
  onShowAddModal: () => void;
  onShowEditModal: (category: BlogCategoryDto) => void;
  onShowDeleteModal: (category: BlogCategoryDto) => void;
  onHideAddModal: () => void;
  onHideEditModal: () => void;
  onHideDeleteModal: () => void;
  onHideErrorModal: () => void;
  onCreateCategory: (values: any, formikBag: any) => void;
  onUpdateCategory: (values: any) => void;
  onDeleteCategory: () => void;
  CategoryForm: React.ComponentType<any>;
  CategoryTable: React.ComponentType<CategoryTableProps>;
}

export const CategoriesContent: React.FC<CategoriesContentProps> = ({
  loading,
  error,
  categories,
  operationSuccess,
  searchSlug,
  isSearching,
  showAddModal,
  showEditModal,
  showDeleteModal,
  selectedCategory,
  saving,
  errorMessage,
  onRetry,
  onSearchChange,
  onSearch,
  onClearSearch,
  onShowAddModal,
  onShowEditModal,
  onShowDeleteModal,
  onHideAddModal,
  onHideEditModal,
  onHideDeleteModal,
  onHideErrorModal,
  onCreateCategory,
  onUpdateCategory,
  onDeleteCategory,
  CategoryForm,
  CategoryTable,
}) => {
  return (
    <div className="page-content dashboard">
      {loading && <LoadingItem />}
      {error && (
        <div className="error-container">
          <AlertMsg type="error" message={error} />
          <button onClick={onRetry} className="btn btn-primary">
            Retry
          </button>
        </div>
      )}
      {!loading && !error && (
        <div className="reports_wrap">
          {operationSuccess && (
            <AlertMsg type="success" message={operationSuccess} />
          )}

          <div className="report_head_wrap">
            <div className="report_head_left">
              <div className="report_heading">
                Categories <span>{categories.length}</span>
              </div>
              <div className="report_desc">
                Manage blog categories for organizing your posts
              </div>
            </div>
            <div className="report_head_right">
              <div className="category-search-container">
                <input
                  type="text"
                  className="search-input"
                  placeholder="Search by slug..."
                  value={searchSlug}
                  onChange={(e) => onSearchChange(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && onSearch()}
                />
                <button
                  type="button"
                  onClick={onSearch}
                  className="search-btn"
                  disabled={isSearching}
                >
                  {isSearching ? "Searching..." : "Search"}
                </button>
                {searchSlug && (
                  <button
                    type="button"
                    onClick={onClearSearch}
                    className="clear-btn"
                  >
                    Clear
                  </button>
                )}
              </div>
              <div className="report_head_cta">
                <button onClick={onShowAddModal}>
                  <img src={AddIco} alt="" />
                  <span>New Category</span>
                </button>
              </div>
            </div>
          </div>

          <div className="reports_table">
            <TableComponent
              childComponent={
                <CategoryTable
                  categories={categories}
                  onEdit={onShowEditModal}
                  onDelete={onShowDeleteModal}
                />
              }
            />
          </div>

          {/* Add Category Modal */}
          <Modal show={showAddModal} onHide={onHideAddModal} centered>
            <Modal.Header closeButton>
              <Modal.Title>Add new category</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <CategoryForm
                initialValues={{ name: "", description: "" }}
                onSubmit={onCreateCategory}
              />
            </Modal.Body>
          </Modal>

          {/* Edit Category Modal */}
          <Modal
            show={showEditModal && selectedCategory !== null}
            onHide={onHideEditModal}
            centered
          >
            <Modal.Header closeButton>
              <Modal.Title>Update category</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {selectedCategory && (
                <CategoryForm
                  initialValues={{
                    name: selectedCategory.name,
                    description: selectedCategory.description || "",
                  }}
                  onSubmit={onUpdateCategory}
                  isEdit={true}
                />
              )}
            </Modal.Body>
          </Modal>

          {/* Delete Confirmation Modal */}
          <Modal
            show={showDeleteModal && selectedCategory !== null}
            onHide={onHideDeleteModal}
            centered
          >
            <Modal.Header closeButton>
              <Modal.Title>Delete category</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <p className={styles.modalMessage}>
                Are you sure you want to delete{" "}
                <strong>{selectedCategory?.name || "this category"}</strong>?
              </p>
              {selectedCategory?.postCount &&
                selectedCategory.postCount > 0 && (
                  <div className={styles.modalWarning}>
                    <AlertMsg
                      type="warning"
                      message={`This category has ${selectedCategory.postCount} post(s). Deleting it may affect those posts.`}
                    />
                  </div>
                )}
              <div className={styles.modalActions}>
                <button
                  type="button"
                  onClick={onHideDeleteModal}
                  disabled={saving}
                  className={styles.cancelButton}
                >
                  Cancel
                </button>
                <button
                  className={`submit-btn ${styles.deleteButton}`}
                  onClick={onDeleteCategory}
                  disabled={saving}
                >
                  {saving ? "Deleting..." : "Delete category"}
                </button>
              </div>
            </Modal.Body>
          </Modal>

          {/* Error Modal */}
          <Modal show={!!errorMessage} onHide={onHideErrorModal} centered>
            <Modal.Header closeButton>
              <Modal.Title>
                <div className={styles.errorHeader}>
                  <FiXCircle size={24} color="#EC3237" />
                  <span>Error</span>
                </div>
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <p className={styles.errorMessage}>{errorMessage}</p>
              <button
                type="button"
                className={`submit-btn ${styles.errorButton}`}
                onClick={onHideErrorModal}
              >
                OK
              </button>
            </Modal.Body>
          </Modal>
        </div>
      )}
    </div>
  );
};
